import { ICartItem } from "@/pages/point-of-sale/models/cart-item.model.ts";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { IdbStorage } from "@/store/idbStorage.ts";
import { VatTypes } from "@/models/vat-type.model.ts";
import { IVatType } from "@/pages/home/models";
import { toast } from "sonner";
import { safeMultiply, roundQty } from "@/utilities/decimal.utils";

// OPTIMIZACIÓN: Import directo en vez de dynamic import en cada operación del carrito
let _resetDiscount: (() => void) | null = null;
const resetDiscount = () => {
  if (!_resetDiscount) {
    import("@/store/sale.store.ts").then(({ useSaleStore }) => {
      _resetDiscount = () => {
        const store = useSaleStore.getState();
        store.setDiscount(0);
        store.setDiscountType("");
      };
      _resetDiscount();
    });
  } else {
    _resetDiscount();
  }
};

export interface IShoppingCartState {
  shoppingCart: ICartItem[];
  /** Líneas del carrito (`productId_containerTypeId`) marcadas por error de stock del API. */
  stockErrorLineKeys: string[];
  setStockErrorLineKeys: (keys: string[]) => void;
  clearStockErrorLineKeys: () => void;
  addCartItem: (newCartItem: ICartItem) => void;
  updateCartItem: (updatedCartItem: ICartItem) => void;
  deleteCartItem: (uuid: string) => void;
  findCartItem: (uuid: string) => ICartItem | undefined;
  updateShoppingCart: (cartItem: ICartItem, replaceQuantity: boolean) => void;
  getShoppingCartSummary: (vatType: VatTypes | undefined) => number;
  updateItemVatType: (uuid: string, vatType: IVatType | undefined) => void;
  setShoppingCart: any;
  resetShoppingCart: () => void;
  resetStore: () => void;
}

export const useShoppingCartStore = create<IShoppingCartState>()(
  devtools(
    persist(
      (set, get) => {
        return {
          shoppingCart: [],
          stockErrorLineKeys: [],
          setStockErrorLineKeys: (keys: string[]) =>
            set({ stockErrorLineKeys: keys }),
          clearStockErrorLineKeys: () => set({ stockErrorLineKeys: [] }),
          addCartItem: (newCartItem: ICartItem) => {
            set(({ shoppingCart }) => ({
              shoppingCart: [...shoppingCart, newCartItem],
              stockErrorLineKeys: [],
            }));
          },
          updateCartItem: (updatedCartItem: ICartItem) => {
            const shoppingCart = get().shoppingCart;
            const updatedShoppingCart = shoppingCart?.map((prevItem) =>
              prevItem.uuid === updatedCartItem.uuid
                ? updatedCartItem
                : prevItem
            );
            set(() => ({
              shoppingCart: updatedShoppingCart,
              stockErrorLineKeys: [],
            }));
          },
          deleteCartItem: (uuid: string) => {
            const shoppingCart = get().shoppingCart;

            const itemToDelete = shoppingCart.find(
              (item) => item.uuid === uuid
            );
            if (!itemToDelete) return;

            // OPTIMIZACIÓN: Filtrar directamente sin spread innecesario
            const updatedShoppingCart = shoppingCart.filter(
              (item) =>
                !(
                  item.productId === itemToDelete.productId &&
                  item.vatType.id === itemToDelete.vatType.id &&
                  item.containerType.id === itemToDelete.containerType.id
                )
            );

            resetDiscount();
            set({
              shoppingCart: updatedShoppingCart,
              stockErrorLineKeys: [],
            });
          },
          findCartItem: (uuid: string) => {
            const shoppingCart = get().shoppingCart;
            return shoppingCart.find((item) => item.uuid === uuid);
          },
          updateShoppingCart: (
            updatedCartItem: ICartItem,
            replaceQuantity = false
          ) => {
            const shoppingCart = get().shoppingCart;
            const addCartItem = get().addCartItem;

            resetDiscount();

            const matchLine = (item: ICartItem) =>
              item.productId === updatedCartItem.productId &&
              item.containerType.id === updatedCartItem.containerType.id &&
              item.containerType.price === updatedCartItem.containerType.price &&
              item.vatType.id === updatedCartItem.vatType.id;

            const resolveTotalPrice = (totalQuantity: number) =>
              updatedCartItem.totalPrice ??
              safeMultiply(totalQuantity, updatedCartItem.containerType.price) -
                (updatedCartItem.amountRedeemed ?? 0);

            const lineIndex = shoppingCart.findIndex(matchLine);

            if (lineIndex === -1) {
              const totalQuantity = updatedCartItem.lots.reduce(
                (sum, l) => roundQty(sum + l.quantity),
                0
              );
              if (totalQuantity <= 0) return;

              addCartItem({
                ...updatedCartItem,
                totalQuantity,
                totalPrice: resolveTotalPrice(totalQuantity),
              });
              return;
            }

            const existingItem = shoppingCart[lineIndex];
            let updatedLots = [...existingItem.lots];

            for (const newLot of updatedCartItem.lots) {
              const lotKey = newLot.lot.id?.toString();
              const lotIdx = updatedLots.findIndex(
                (l) => l.lot.id?.toString() === lotKey
              );
              const currentCartQuantity =
                lotIdx >= 0
                  ? replaceQuantity
                    ? 0
                    : updatedLots[lotIdx].quantity
                  : 0;
              const newLotQuantity = replaceQuantity
                ? newLot.quantity
                : roundQty(currentCartQuantity + newLot.quantity);

              if (roundQty(newLotQuantity) > newLot.lot.stock) {
                toast.error(
                  `No puedes agregar más de ${newLot.lot.stock} unidades de este lote.`
                );
                return;
              }

              if (lotIdx >= 0) {
                updatedLots[lotIdx] = {
                  ...updatedLots[lotIdx],
                  quantity: newLotQuantity,
                };
              } else {
                updatedLots.push({ ...newLot, quantity: newLotQuantity });
              }
            }

            const totalQuantity = updatedLots.reduce(
              (sum, l) => roundQty(sum + l.quantity),
              0
            );

            const updatedShoppingCart = shoppingCart.slice();
            updatedShoppingCart[lineIndex] = {
              ...existingItem,
              ...updatedCartItem,
              lots: updatedLots,
              totalQuantity,
              totalPrice: resolveTotalPrice(totalQuantity),
            };

            set({
              shoppingCart: updatedShoppingCart,
              stockErrorLineKeys: [],
            });
          },

          getShoppingCartSummary: (vatType: VatTypes | undefined) => {
            const shoppingCart = get().shoppingCart;
            const sum = !vatType
              ? shoppingCart.reduce(
                  (subTotal, item) => subTotal + (item.totalPrice ?? 0),
                  0
                )
              : shoppingCart.reduce(
                  (subTotal, item) =>
                    subTotal +
                    (item.vatType.type === vatType ? item.totalPrice ?? 0 : 0),
                  0
                );
            return roundQty(sum, 2);
          },
          updateItemVatType: (uuid: string, vatType: IVatType | undefined) => {
            if (!vatType) return;

            const shoppingCart = get().shoppingCart;
            const updatedShoppingCart = shoppingCart?.map((prevItem) =>
              prevItem.uuid === uuid
                ? {
                    ...prevItem,
                    vatType: vatType,
                  }
                : prevItem
            );
            set(() => ({
              shoppingCart: updatedShoppingCart,
              stockErrorLineKeys: [],
            }));
          },
          setShoppingCart: (cart: ICartItem[]) =>
            set(() => ({ shoppingCart: cart, stockErrorLineKeys: [] })),
          resetShoppingCart: () =>
            set(() => ({ shoppingCart: [], stockErrorLineKeys: [] })),
          resetStore: () =>
            set({
              shoppingCart: [],
              stockErrorLineKeys: [],
            }),
        };
      },
      {
        name: "shopping-cart-store",
        partialize: (state) => ({
          shoppingCart: state.shoppingCart,
        }),
        storage: createJSONStorage(() => IdbStorage),
      }
    )
  )
);
